import { R, H, ringPoints } from './geometry'
import { fireDelayMs, scanDelayMs } from './useReveal'
import { COLOR } from '../../../config/uiTokens'
import { RocketGlyph, PlaneGlyph, BaseDroneGlyph, EggGlyph, MaturationBar, HexBevel, baseBarCenter, ROCKET_FILL, PLANE_FILL, BASE_DRONE_FILL, BASE_PAINT } from './assets'
import { isOccupied, occupantOf } from '../../../rules/subplotGrid'

// ─── Hex board — per-cell marks ───────────────────────────────────────────────
// Three passes over the Grid's cells, mirroring the square board's layers:
//   territoryMarks — Port, scans, own-structure wash, craters   (PlotSubplots)
//   strokeMarks    — structure rings/glows, Bases, the red X    (PlotStructStrokes)
//   baseBarMarks   — construction maturation bars, above the strokes
//   stampMarks     — the tagged intel stamps, in their own layer above the
//                    airspace lanes so a pill edge never runs over a number
// Each returns an array of SVG elements for one cell (empty = nothing to draw)
// and takes a ctx: { peekColors (Map s→hue while the
// header eye is held), hiddenStamps (Set of stamps whose structure the peek
// has located — they hide while the eye is held), fresh (Set of cells destroyed by the latest swap),
// fireOrigin (the aim they emanate from), freshScan / scanOrigins (likewise
// for drone scans) }. A fresh crater or scan fades in on the sweep's clock —
// class hexb__fresh plus an inline delay — with an ignition flash on top.
// Every colour is copied from BoardGrid.css and annotated with its rule.

// Tagged stamp text, exactly as BoardGrid's stampText.
export const stampText = (cell) =>
    cell.distanceLetter != null ? `${cell.distanceLetter}:${cell.distance}` : `${cell.distance}`

export function Stamp({ cx, cy, cell, color = null }) {
    // .bg__ps-distance — Oswald 700 10px #ffd84a, top-justified, dark halo
    // (dead stamps don't draw: stampMarks). During the intel peek a live stamp
    // takes its ring's hue so the pair reads as one claim.
    return (
        <text
            x={cx}
            y={cy - H / 2 + 2}
            textAnchor="middle"
            dominantBaseline="hanging"
            fontFamily="'Oswald', sans-serif"
            fontSize="10"
            fontWeight="700"
            letterSpacing="-0.02em"
            fill={color ?? '#ffd84a'}
            filter="url(#hexb-stamp-shadow)"
            style={{ pointerEvents: 'none' }}
        >{stampText(cell)}</text>
    )
}

export function RedX({ cx, cy }) {
    // .bg__ps-x — settled look: 68% long, ~1.5px thick, rgba(205,75,75,0.5).
    const len = 1.17 * R
    const d = len / 2 / Math.SQRT2
    return (
        <g stroke="rgba(205, 75, 75, 0.5)" strokeWidth="1.7" strokeLinecap="round">
            <line x1={cx - d} y1={cy - d} x2={cx + d} y2={cy + d} />
            <line x1={cx - d} y1={cy + d} x2={cx + d} y2={cy - d} />
        </g>
    )
}

export function Bullseye({ cx, cy }) {
    // .bg__ps-bullseye — structure yellow dot + two rings, 64% of the cell tall.
    const Ro = 0.32 * H
    return (
        <g fill="none" stroke="#f0e040">
            <circle cx={cx} cy={cy} r={0.22 * Ro} fill="#f0e040" stroke="none" />
            <circle cx={cx} cy={cy} r={0.54 * Ro} strokeWidth={0.20 * Ro} />
            <circle cx={cx} cy={cy} r={0.88 * Ro} strokeWidth={0.16 * Ro} />
        </g>
    )
}

// Territory layer for one cell (mirrors PlotSubplots minus roads/animation).
export function territoryMarks(side, hf, s, cell, pts, cx, cy, { fresh, fireOrigin, freshScan, scanOrigins } = {}) {
    const isOpp = side === 'opp'
    const out = []
    if (!cell) return out
    // Sweep timing for a cell revealed by the latest swap (crater or scan).
    const isFresh = !!fresh?.has(s)
    const isFreshScan = !!freshScan?.has(s)
    const sweepDelay = isFresh ? (fireOrigin != null ? fireDelayMs(s, fireOrigin) : 0)
                     : isFreshScan ? scanDelayMs(s, scanOrigins) : null
    const sweep = (els, flashId) => sweepDelay == null ? els : [
        <g key="sweep" className="hexb__fresh" style={{ animationDelay: `${sweepDelay}ms` }}>
            {els}
            <polygon className="hexb__flash" points={pts} fill={`url(#${flashId})`} style={{ animationDelay: `${sweepDelay}ms` }} />
        </g>,
    ]

    if (!cell.destroyed) {
        const ownStruct = !isOpp && isOccupied(cell)
        if (isOpp && cell.scanned) {
            // .bg__ps-cell--scanned / --scan-center / --scan-aged
            const isCenter = cell.distance != null
            const isStruct = isOccupied(cell)
            const aged = cell.scanned !== true
            out.push(<polygon key="scan" points={pts} fill="rgba(255, 255, 255, 0.26)" />)
            if (!aged) {
                out.push(<polygon key="scan-frame" points={ringPoints(cx, cy, 1)} fill="none"
                    stroke={isCenter ? 'rgba(168, 85, 247, 0.75)' : 'rgba(168, 85, 247, 0.55)'} strokeWidth="1" />)
            }
            if (isStruct) out.push(<Bullseye key="bullseye" cx={cx} cy={cy} />)
            return sweep(out, 'hexb-flash-scan')
        }
        if (!isOpp && cell.scanned) {
            // .bg__ps-cell--scan-self (+ --scan-aged): wash, quieter frame, soft glow.
            const aged = cell.scanned !== true
            out.push(<polygon key="scan" points={pts} fill="rgba(255, 255, 255, 0.26)" />)
            if (!aged) {
                out.push(<polygon key="scan-glow" points={pts} fill="url(#hexb-glow-scan-self)" />)
                out.push(<polygon key="scan-frame" points={ringPoints(cx, cy, 1)} fill="none" stroke="rgba(168, 85, 247, 0.4)" strokeWidth="1" />)
            }
            return sweep(out, 'hexb-flash-scan')
        }
        if (ownStruct) {
            // .bg__ps-cell--own-struct, in your structures' teal (COLOR.STRUCT_SELF)
            out.push(<polygon key="own" points={pts} fill={`rgba(${COLOR.STRUCT_SELF_RGB}, 0.10)`} />)
        }
        return out
    }

    // Destroyed — .bg__ps-cell--damaged (+ --impact). Uniform on both halves.
    // data-mote-s lets a killed structure's silhouette lift off its cells.
    const isImpact = cell.impact || cell.distance != null
    if (isImpact) out.push(<polygon key="impact" points={pts} fill="#0a0806" />)
    out.push(<polygon key="crater" points={pts} fill="url(#hexb-crater)" data-mote-s={`${side}-${s}`} />)
    return sweep(out, 'hexb-flash-fire')
}

// Intel stamps ("A:2") for one cell. Same rules as PlotSubplots +
// PlotStructStrokes: a stamp sits on a crater (the aim cell, or a destroyed
// structure segment), on a self-side scan, or on an opp scan centre — unless
// that centre is a structure cell, where the bullseye outranks the bearing.
// Stamps stay on the board until their structure is found: a stamp whose
// structure is located (some cell of it seen: hiddenStamps) or destroyed
// never draws. The ones the latest clash just made (`entering`, per side)
// come in on the fire / scan sweep's clock (.hexb__stamp-enter) and stay.
export function stampMarks(side, hf, s, cell, pts, cx, cy, {
    peekColors, hiddenStamps, entering = null, fresh, fireOrigin, freshScan, scanOrigins,
} = {}) {
    if (!cell || cell.distance == null || cell.distanceDead || hiddenStamps?.has(s)) return []
    if (!cell.destroyed && !cell.scanned) return []
    if (!cell.destroyed && side === 'opp' && isOccupied(cell)) return []
    const color = peekColors?.get(s) ?? null
    const stamp = <Stamp key="stamp" cx={cx} cy={cy} cell={cell} color={color} />
    if (!entering?.[side]?.has(s)) return [stamp]
    const delay = fresh?.has(s) ? (fireOrigin != null ? fireDelayMs(s, fireOrigin) : 0)
                : freshScan?.has(s) ? scanDelayMs(s, scanOrigins) : 0
    return [<g key="stamp" className="hexb__stamp-enter" style={{ animationDelay: `${delay}ms` }}>{stamp}</g>]
}

const BROOD_GHOST = 0.22      // a brood-in-construction's drones-to-be

// Structure stroke layer for one cell (mirrors PlotStructStrokes): the
// building's ring / damage glow / crater ring, a Base's fill with its drone
// silhouettes (or its maturation bar under construction), the rockets and
// pending recon planes the cell hosts, and the red X. ctx adds:
//   pendingRockets / pendingDroneCells  self cells with this round's buys
//   baseProgress                        centre → construction progress
//   baseAttacking                       corridors whose mature Base attacks
//   baseFx(st)                          reveal: null | { fired } |
//                                       { fired: false, shakeKey } — the Base
//                                       recoils per shot (remounted by key)
//                                       and fades once its last shot is away
//   conCollapse                         { id, cells: Map s → delay } — a
//                                       construction that just fell flashes
//                                       red on the fire sweep's clock
export function strokeMarks(side, hf, s, cell, pts, cx, cy, {
    pendingRockets = [], pendingDroneCells = [], baseProgress = null, baseAttacking = null, baseFx = null, conCollapse = null,
    hiddenEggs = null,   // Set(centerS) — nests whose egg is in the air (or has flown, for a spent nest): no egg drawn
} = {}) {
    const isOpp = side === 'opp'
    const out = []
    if (!cell || !isOccupied(cell)) return out
    const st = occupantOf(hf, cell)
    if (!st) return out
    const footprint = st.footprint ?? []
    const isBase = cell.broodId != null
    const buildingDamaged = footprint.some(f => hf.subplots[f]?.destroyed)
    const deployedRocket = !cell.destroyed && !!st.rocketCells?.includes(s)
    const pendingRocket = !isOpp && !cell.destroyed && pendingRockets.includes(s)
    const hasRocket = deployedRocket || pendingRocket
    const hasPlane = !isOpp && !cell.destroyed && pendingDroneCells.includes(s)

    const ring = (color, w = 1) =>
        <polygon key={`ring-${color}`} points={ringPoints(cx, cy, w)} fill="none" stroke={color} strokeWidth={w} />
    const glow = (id) => <polygon key={id} points={pts} fill={`url(#${id})`} />

    if (isOpp) {
        // Intact, unscanned, rocket-free opp segments stay secret.
        if (cell.destroyed) {
            // .bg__ps-stroke--dead: black 1px ring + inner black shadow
            out.push(glow('hexb-glow-dead'))
            out.push(ring('rgba(0, 0, 0, 0.95)'))
        } else if (isBase && st.spent) {
            // A spent nest: the fired brood is gone, and its egg with it.
        } else if (isBase) {
            // The side's Base paint (BASE_PAINT: the drone tiles' colours) with
            // the tiles' bevel instead of a ring.
            const con = st.underConstruction
            const attacking = !con && baseAttacking?.has(st.corridor)
            const paint = BASE_PAINT[side]
            out.push(<polygon key="base" points={pts} fill={attacking ? paint.attackFill : con ? paint.conFill : paint.fill}
                fillOpacity={con && !attacking ? paint.conOpacity : 1} />)
            out.push(<HexBevel key="bevel" cx={cx} cy={cy} />)
        } else if (buildingDamaged) {
            out.push(glow('hexb-glow-hit'))                       // .bg__ps-stroke--hit
        } else if (hasRocket) {
            out.push(ring('rgba(255, 84, 197, 0.8)'))             // .bg__ps-stroke--rocket-opp
        } else if (cell.scanned) {
            out.push(glow('hexb-glow-scanned'))                   // .bg__ps-stroke--scanned
        }
        if (hasRocket) out.push(<RocketGlyph key="rocket" cx={cx} cy={cy} fill={ROCKET_FILL.opp} flip />)
    } else {
        const ringColor = `rgba(${COLOR.STRUCT_SELF_RGB}, 0.6)`            // .bg__ps-stroke--on, in your structures' teal
        if (cell.destroyed) {
            // .bg__ps-stroke--dead: black 1px ring + inner black shadow
            out.push(glow('hexb-glow-dead'))
            out.push(ring('rgba(0, 0, 0, 0.95)'))
        } else if (isBase && st.spent) {
            // A spent nest: the fired brood is gone, and its egg with it.
        } else if (isBase) {
            // The side's Base paint (BASE_PAINT: the drone tiles' colours) with
            // the tiles' bevel instead of a ring.
            const con = st.underConstruction
            const attacking = !con && baseAttacking?.has(st.corridor)
            const paint = BASE_PAINT[side]
            out.push(<polygon key="base" points={pts} fill={attacking ? paint.attackFill : con ? paint.conFill : paint.fill}
                fillOpacity={con && !attacking ? paint.conOpacity : 1} />)
            out.push(<HexBevel key="bevel" cx={cx} cy={cy} />)
        } else if (buildingDamaged) {
            out.push(ring(ringColor))                             // --on + --hit
            out.push(glow('hexb-glow-hit'))
        } else {
            out.push(ring(ringColor))                             // --on
        }
        if (hasRocket) out.push(<RocketGlyph key="rocket" cx={cx} cy={cy} fill={pendingRocket ? ROCKET_FILL.pending : ROCKET_FILL.self} />)
        if (hasPlane) out.push(<PlaneGlyph key="plane" cx={cx} cy={cy} fill={PLANE_FILL.pending} />)
    }
    // The brood's occupants: a drone on each outer cell; on the nest, the
    // egg (mature and intact — hidden while it is in the air, or once it has
    // flown from a spent nest), or the hatchling (the egg matured in place
    // when a drone was cratered — it pops in, .hexb__hatch), or nothing (the
    // nest cratered: the crater says so). A brood that fires takes its egg
    // with it (subplotBases: it never launches), so the egg rides the
    // brood's fade-out group and goes with it; a spent brood shows none.
    let nestGlyph = null
    // A brood under construction shows its two drones-to-be ghosted on the
    // outer cells.
    if (isBase && !cell.destroyed && st.underConstruction && s !== st.footprint?.[0]) {
        out.push(<BaseDroneGlyph key="basedrone-ghost" cx={cx} cy={cy} fill={BASE_DRONE_FILL[side]} pose="folded" opacity={BROOD_GHOST} />)
    }
    if (isBase && !cell.destroyed && !st.underConstruction) {
        const isNest = s === st.footprint?.[0]
        if (!isNest) {
            // A mature Brood's side drones sit at rest: the folded wasp.
            if (!st.spent) out.push(<BaseDroneGlyph key="basedrone" cx={cx} cy={cy} fill={BASE_DRONE_FILL[side]} pose="folded" />)
        } else if (st.egg === 'hatched') {
            nestGlyph = <g key="hatchling" className="hexb__hatch" data-hatchling={s}><BaseDroneGlyph cx={cx} cy={cy} fill={BASE_DRONE_FILL[side]} /></g>
        } else if (st.egg === 'egg' && !st.spent && !hiddenEggs?.has(s)) {
            nestGlyph = <EggGlyph key="egg" cx={cx} cy={cy} />
        }
    }
    if (cell.destroyed) out.push(<RedX key="x" cx={cx} cy={cy} />)
    if (conCollapse?.cells.has(s)) {
        // .bg__ps-con-dying: the fallen construction's red flash, after the
        // fire front reaches it.
        out.push(
            <polygon key={`condying-${conCollapse.id}`} className="hexb__con-dying" points={pts}
                fill="rgba(224, 48, 32, 0.8)" stroke="rgba(255, 90, 60, 0.95)" strokeWidth="1.5"
                style={{ animationDelay: `${conCollapse.cells.get(s)}ms` }} />
        )
    }
    // A live Base's cell recoils as each of its shots launches and fades
    // once spent (.bg__ps-stroke--base-fire / --base-spent); a construction
    // fades up as it appears (--base-con).
    if (isBase && !cell.destroyed) {
        const fx = st.spent ? null : (baseFx?.(st) ?? null)
        const cls = fx ? (fx.fired ? 'hexb__base-spent' : 'hexb__base-fire') : (st.underConstruction ? 'hexb__base-con' : null)
        // Once its shots are away the egg fades with the brood, not after it.
        const inGroup = fx?.fired && nestGlyph ? [...out, nestGlyph] : out
        const group = inGroup.length ? <g key={fx && !fx.fired ? `base-k${fx.shakeKey}` : `base-${st.id}`} className={cls ?? undefined}>{inGroup}</g> : null
        return [group, fx?.fired ? null : nestGlyph].filter(Boolean)
    }
    if (nestGlyph) out.push(nestGlyph)
    return out
}

// ── Bar pass ────────────────────────────────────────────────────────────────
// A construction's maturation bar, at the footprint's centre of gravity,
// drawn in a layer of its own ABOVE the stroke pass: the bar is wider than one hex, and in the
// stroke pass the footprint's right-hand cell (a later group) painted over
// its end. Wears the construction's fade-up with it.
export function baseBarMarks(side, hf, s, cell, pts, cx, cy, { baseProgress = null } = {}) {
    if (!cell || !isOccupied(cell) || cell.destroyed) return []
    const st = occupantOf(hf, cell)
    if (!st || cell.broodId == null || !st.underConstruction || s !== st.footprint?.[0]) return []
    const { x, y } = baseBarCenter(side, s)
    return [
        <g key={`bar-${st.id}`} className="hexb__base-con">
            <MaturationBar cx={x} cy={y} progress={baseProgress?.[s] ?? 0} centerS={s} />
        </g>,
    ]
}

// The cells whose blast clouds erupt this swap: every freshly destroyed
// STRUCTURE cell, each on its own fire-sweep delay.
// A construction that collapsed this swap blasts on every footprint cell
// once its red flash has run (CON_COLLAPSE_FLASH_TOTAL_MS after the sweep
// reaches it), the whole bar as one structure.
export const CON_COLLAPSE_FLASH_TOTAL_MS = 300 * 3
export function blastCells(side, hf, fresh, fireOrigin, cells, conCollapse = null) {
    if (!hf?.subplots || (!fresh?.size && !conCollapse?.cells.size)) return []
    const out = []
    for (const s of (fresh ?? [])) {
        if (conCollapse?.cells.has(s)) continue
        const cell = hf.subplots[s]
        if (!isOccupied(cell) || !cell.destroyed) continue
        const c = cells[s]
        out.push({ key: `${side}-${s}`, cx: c.cx, cy: c.cy, delay: fireOrigin != null ? fireDelayMs(s, fireOrigin) : 0 })
    }
    for (const [s, delay] of (conCollapse?.cells ?? [])) {
        const c = cells[s]
        out.push({ key: `${side}-c${conCollapse.id}-${s}`, cx: c.cx, cy: c.cy, delay: delay + CON_COLLAPSE_FLASH_TOTAL_MS })
    }
    return out
}
